import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClubPanel, PanelHead, PillButton, TagPill, Face, ROLE_PILL, useArmed, MUTED, SUBTLE, RULE, RED } from './clubUi';
import LoadFailed from './LoadFailed';
import { monthYearLabel, plural } from '../../utils/surfaceFormat';

/**
 * MEMBERS (README §1): admin PENDING REQUESTS · N (52px avatar, meta, Vibe Check in Times italic,
 * ACCEPT black · NOT FOR NOW grey · BLOCK red) · MEMBERS · N (role pill, `@handle · joined May 2026
 * · 3 parties`, admin MANAGE menu — white radius-18 + 2px black border; MAKE ADMIN / DEMOTE TO
 * MEMBER / TRANSFER OWNERSHIP / KICK / BLOCK red, the last two armed on first tap) · INVITE FRIENDS ·
 * admin BLOCKED USERS · N (SHOW / HIDE, UNBLOCK).
 */
export function memberMeta(m) {
  const parts = [];
  if (m.handle) parts.push(`@${m.handle}`);
  if (m.joinedAt) parts.push(`joined ${monthYearLabel(m.joinedAt).replace(/^(\w)(\w+)/, (_, a, b) => a + b.toLowerCase())}`);
  if (m.partiesAttended != null) parts.push(m.partiesAttended === 1 ? '1 event' : `${m.partiesAttended} events`);
  return parts.join(' · ');
}

function ManageMenu({ member, actions }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { isArmed, confirm, disarm } = useArmed();
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); disarm(); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, disarm]);
  if (!actions.length) return null;
  return (
    <span className="relative flex-none" ref={ref}>
      <PillButton variant={open ? 'primary' : 'tertiary'} size={34} fontSize={9} onClick={() => { setOpen((v) => !v); disarm(); }} aria-haspopup="menu" aria-expanded={open} data-testid={`manage-${member.userId}`}>MANAGE</PillButton>
      {open && (
        <span role="menu" className="absolute right-0 z-20 flex flex-col bg-white" style={{ top: 'calc(100% + 6px)', minWidth: 210, padding: 6, borderRadius: 18, border: '2px solid #000', boxSizing: 'border-box' }} data-testid={`manage-menu-${member.userId}`}>
          {actions.map((a) => {
            const armed = a.arm && isArmed(a.label);
            return (
              <button key={a.label} type="button" role="menuitem" disabled={a.busy} aria-label={a.label}
                onClick={() => { if (a.arm && !confirm(a.label)) return; setOpen(false); a.onClick(); }}
                className="text-left font-mono uppercase transition-colors hover:bg-[#F5F5F5] disabled:opacity-50"
                style={{ height: 38, padding: '0 14px', border: 'none', borderRadius: 12, background: armed ? RED : 'transparent', color: armed ? '#fff' : a.danger ? RED : '#000', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer' }}>
                {armed ? `CONFIRM ${a.label}` : a.label}
              </button>
            );
          })}
        </span>
      )}
    </span>
  );
}

export default function ClubMembersTab({
  club, state, members = [], pendingRequests = [], blockedUsers = [], currentUserId, busyUserId,
  onAccept, onDecline, onBlockRequest, onUnblock, onMemberAction, onInvite, mobile = false,
  membersError = false, onRetryLoad,
}) {
  const navigate = useNavigate();
  const [showBlocked, setShowBlocked] = useState(false);
  const { isArmed, confirm } = useArmed();
  const profileHref = (m) => (m.handle ? `/${m.handle}` : `/users/${m.userId}`);

  const actionsFor = (m) => {
    const isSelf = m.userId === currentUserId;
    const isMemberOwner = m.role === 'owner';
    const isMemberAdmin = m.role === 'admin';
    const busy = busyUserId === m.userId;
    const canKick = state.isAdmin && !isSelf && !isMemberOwner;
    const canChangeRole = state.isOwner && !isSelf && !isMemberOwner;
    const list = [];
    if (canChangeRole && !isMemberAdmin) list.push({ label: 'MAKE ADMIN', busy, onClick: () => onMemberAction(m, 'promote') });
    if (canChangeRole && isMemberAdmin) list.push({ label: 'DEMOTE TO MEMBER', busy, onClick: () => onMemberAction(m, 'demote') });
    if (canChangeRole) list.push({ label: 'TRANSFER OWNERSHIP', busy, onClick: () => onMemberAction(m, 'transfer') });
    if (canKick) list.push({ label: 'KICK', busy, arm: true, onClick: () => onMemberAction(m, 'kick') });
    if (canKick) list.push({ label: 'BLOCK', busy, danger: true, arm: true, onClick: () => onMemberAction(m, 'block') });
    return list;
  };

  const row = (m, i) => {
    const [label, bg, fg] = ROLE_PILL[m.role] || ROLE_PILL.member;
    return (
      <div key={m.userId} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '12px 0', borderTop: i ? RULE : 'none' }} data-testid={`member-${m.userId}`}>
        <button type="button" onClick={() => navigate(profileHref(m))} aria-label={m.displayName} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}><Face src={m.avatarUrl} name={m.displayName} size={44} tintKey={m.userId} /></button>
        <span className="flex min-w-0 flex-col" style={{ flex: '1 1 180px', gap: 4 }}>
          <span className="flex flex-wrap items-center" style={{ gap: 8 }}>
            <button type="button" onClick={() => navigate(profileHref(m))} className="truncate text-left font-mono hover:underline" style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35, border: 'none', background: 'none', padding: 0, cursor: 'pointer', color: 'inherit' }}>{m.displayName}{m.userId === currentUserId ? ' · YOU' : ''}</button>
            <TagPill bg={bg} fg={fg} data-testid={`role-${m.userId}`}>{label}</TagPill>
          </span>
          <span className="truncate" style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>{memberMeta(m)}</span>
        </span>
        {state.isAdmin && <ManageMenu member={m} actions={actionsFor(m)} />}
      </div>
    );
  };

  return (
    <div className="flex flex-col font-mono" style={{ gap: mobile ? 10 : 16 }} data-testid="club-members">
      {state.isAdmin && pendingRequests.length > 0 && (
        <ClubPanel phone={mobile} data-testid="members-pending">
          <PanelHead title={`PENDING REQUESTS · ${pendingRequests.length}`} right={mobile ? undefined : 'CHECKED EVERY 15S'} />
          <div className="flex flex-col">
            {pendingRequests.map((p, i) => {
              const armed = isArmed(`block-${p.id}`);
              return (
                <div key={p.id} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '12px 0', borderTop: i ? RULE : 'none' }} data-testid={`pending-${p.id}`}>
                  <button type="button" onClick={() => navigate(p.handle ? `/${p.handle}` : `/users/${p.userId}`)} aria-label={p.displayName} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer' }}><Face src={p.avatarUrl} name={p.displayName} size={52} tintKey={p.userId} /></button>
                  <span className="flex min-w-0 flex-col" style={{ flex: '1 1 180px', gap: 4 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>{p.displayName}</span>
                    <span style={{ fontSize: 11, lineHeight: 1.5, color: MUTED }}>{[p.handle ? `@${p.handle}` : null, p.partiesAttended != null ? (p.partiesAttended === 1 ? '1 event' : `${p.partiesAttended} events`) : null, p.createdAt ? `asked ${monthYearLabel(p.createdAt).toLowerCase()}` : null].filter(Boolean).join(' · ')}</span>
                    {p.vibeCheck && <span style={{ fontFamily: '"Times New Roman", Times, serif', fontStyle: 'italic', fontSize: 16, lineHeight: 1.4 }}>{p.vibeCheck}</span>}
                  </span>
                  <span className="flex flex-none flex-wrap" style={{ gap: 6 }}>
                    <PillButton size={34} fontSize={9} onClick={() => onAccept(p)} data-testid={`accept-${p.id}`}>ACCEPT</PillButton>
                    <PillButton variant="tertiary" size={34} fontSize={9} onClick={() => onDecline(p)} data-testid={`decline-${p.id}`}>NOT FOR NOW</PillButton>
                    <PillButton variant={armed ? 'armed' : 'dangerGhost'} size={34} fontSize={9} onClick={() => { if (confirm(`block-${p.id}`)) onBlockRequest(p); }} data-testid={`block-${p.id}`}>{armed ? 'CONFIRM BLOCK' : 'BLOCK'}</PillButton>
                  </span>
                </div>
              );
            })}
          </div>
        </ClubPanel>
      )}

      <ClubPanel phone={mobile} data-testid="members-roster">
        <PanelHead title={`MEMBERS · ${club.memberCount ?? members.length}`} />
        {state.canInvite && <span className="flex flex-wrap" style={{ gap: 6 }}><PillButton size={36} fontSize={10} onClick={onInvite} data-testid="invite-friends">INVITE FRIENDS</PillButton></span>}
        {membersError && <LoadFailed what="the members" onRetry={onRetryLoad} testId="members-failed" />}
        {!membersError && members.length === 0 && <span style={{ fontSize: 12, color: SUBTLE }}>{club.showMembersList === false && !state.isAdmin ? 'THE MEMBERS LIST IS ADMINS ONLY.' : 'NO MEMBERS YET'}</span>}
        {members.length > 0 && <div className="flex flex-col">{members.map((m, i) => row(m, i))}</div>}
      </ClubPanel>

      {state.isAdmin && (
        <ClubPanel phone={mobile} data-testid="members-blocked">
          <PanelHead title={`BLOCKED USERS · ${blockedUsers.length}`}>
            <button type="button" onClick={() => setShowBlocked((v) => !v)} className="font-mono uppercase" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }} data-testid="toggle-blocked">{showBlocked ? 'HIDE' : 'SHOW'}</button>
          </PanelHead>
          {showBlocked && (blockedUsers.length === 0
            ? <span style={{ fontSize: 12, color: SUBTLE }}>NO BLOCKED USERS</span>
            : (
              <div className="flex flex-col">
                {blockedUsers.map((b, i) => (
                  <div key={b.userId} className="flex flex-wrap items-center" style={{ gap: '10px 14px', padding: '12px 0', borderTop: i ? RULE : 'none', opacity: 0.8 }} data-testid={`blocked-${b.userId}`}>
                    <Face src={b.avatarUrl} name={b.displayName} size={44} tintKey={b.userId} />
                    <span className="flex min-w-0 flex-col" style={{ flex: '1 1 180px', gap: 4 }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{b.displayName}</span>
                      {b.blockedAt && <span style={{ fontSize: 11, color: MUTED }}>Blocked {monthYearLabel(b.blockedAt)}</span>}
                    </span>
                    <PillButton variant="tertiary" size={34} fontSize={9} onClick={() => onUnblock(b)} data-testid={`unblock-${b.userId}`}>UNBLOCK</PillButton>
                  </div>
                ))}
              </div>
            ))}
        </ClubPanel>
      )}
      {!state.isAdmin && members.length > 0 && <span style={{ fontSize: 10, color: SUBTLE }}>{plural(members.length, 'MEMBER')} SHOWN</span>}
    </div>
  );
}
